import type { ReploElementMetadata } from "schemas/generated/element";

import * as React from "react";

import { cloneMetafieldsRemovingIds } from "@v1/components/common/utils";
import { createElement, setElementsLoading } from "@v1/reducers/core-reducer";
import { useEditorDispatch } from "@v1/store/store";
import { folderUtils } from "@v1/trpc/routes/folder";
import {
  getDuplicatedElementName,
  getDuplicateElementPath,
  sanitizeElementName,
} from "@v1/utils/element";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { useNavigate } from "react-router-dom";
import { refreshComponentIds } from "replo-runtime/shared/refreshComponentIds";
import { ReploError } from "schemas/errors";

class DuplicateElementError extends ReploError {
  constructor(value: string) {
    super({
      message: "Failed to duplicate element",
      additionalData: { value },
    });
  }
}

export function useDuplicateElement() {
  const dispatch = useEditorDispatch();
  const navigate = useNavigate();
  const { mutateAsync: duplicateElement, isPending: isDuplicating } =
    elementRouter.duplicate.useMutation({
      onMutate: () => {
        dispatch(setElementsLoading(true));
      },
      onSuccess: (data) => {
        if (data.element) {
          dispatch(
            createElement({
              element: data.element,
            }),
          );
        }
        void elementUtils.listAllElementsMetadata.invalidate();
        void folderUtils.list.invalidate({
          projectId: data.element?.projectId,
        });
      },
      onSettled: () => {
        dispatch(setElementsLoading(false));
      },
    });

  // Add utility to get element's component for ID refreshing

  const handleDuplicateElement = React.useCallback(
    async function handleDuplicateElementFn(
      element: ReploElementMetadata,
      isSupportMode = false,
    ) {
      const now = new Date();
      const duplicatedName = getDuplicatedElementName({
        name: element.name,
        now,
        elementType: element.type,
        isSupportCopy: isSupportMode,
      });
      const duplicatedPath = getDuplicateElementPath({
        name: element.name,
        now,
        path: element.shopifyPagePath,
        isSupportCopy: isSupportMode,
      });

      // Fetch the element's component and refresh IDs on frontend
      const { element: fullElement } = await elementUtils.getById.fetch(
        element.id,
        { staleTime: 0 },
      );
      const refreshedComponent = fullElement.component
        ? refreshComponentIds(fullElement.component).component
        : undefined;

      const payload = await duplicateElement({
        element: {
          ...element,
          name: sanitizeElementName(duplicatedName, element.type),
          // Pass the component with refreshed IDs instead of undefined
          component: refreshedComponent,
          // Note (Ovishek, 2023-01-09): We remove all the ids from metafields when duplicating old page metafields.
          // This is because in the backend we update metafields with ids but we should create fresh metafields here.
          shopifyMetafields: cloneMetafieldsRemovingIds(
            element.shopifyMetafields,
          ),
          shopifyPagePath: duplicatedPath,
          // Note (Noah, 2022-10-19, REPL-4659): Default to not having pages published
          // for duplicated pages, because otherwise if a page is published but user
          // is on the trial plan, the page will have is_published = True and the user
          // will be able to publish even if they're not paying
          isPublished: false,
          // Note (Noah, 2023-04-27, USE-112): If this page is a homepage, we don't want
          // the duplicated page to be a homepage two, since that would create two homepages
          isHomepage: false,
          // Note (Ben O, 2025-03-02) Remove the publishedAt field so users can rename the duplicated section
          publishedAt: undefined,
          // Note (Noah, 2025-12-31): Normalize shopifyArticleImage to ensure both keys are present
          shopifyArticleImage: element.shopifyArticleImage
            ? {
                src: element.shopifyArticleImage.src,
                alt: element.shopifyArticleImage.alt,
              }
            : undefined,
        },
        duplicatingElementId: element.id,
        isSupportMode,
      });

      if (payload.element) {
        analytics.logEvent("element.new", {
          operation: "duplicate",
          type: element.type,
          creationMethod: "duplicate",
        });
        if (location.pathname.includes("/editor")) {
          navigate(
            `/editor/${payload.element.projectId}/${payload.element.id}`,
          );
        }
      } else {
        throw new DuplicateElementError(element.id);
      }
    },
    [duplicateElement, navigate],
  );

  return {
    handleDuplicateElement,
    isDuplicating,
  };
}
